import type { ServicePageData } from './types';

const tablecloths: ServicePageData = {
  slug: 'tablecloths',
  eyebrow: 'Signage',
  title: 'Turn a folding table into a',
  titleAccent: 'professional trade stand.',
  intro:
    'Branded tablecloths and runners make use of the space in front of your table that would otherwise go to waste. Choose a style, send us your logo, and we will turn a plain trestle table into part of your marketing.',
  heroImages: [
    '/home/tablecloths/tablecloths-copper-beech.jpg',
    '/home/tablecloths/tablecloths-journey.jpg',
    '/home/tablecloths/tablecloths-reptile-shed.jpg',
    '/home/services/tablecloths.jpg',
    '/home/tablecloths/tablecloths-conference-studio.jpg',
    '/home/tablecloths/tablecloths-box-fitted-studio.jpg',
    '/home/tablecloths/tablecloths-runner-studio.jpg',
    '/home/tablecloths/tablecloths-copper-beech.jpg',
    '/home/tablecloths/tablecloths-journey.jpg',
    '/home/tablecloths/tablecloths-reptile-shed.jpg',
    '/home/services/tablecloths.jpg',
    '/home/tablecloths/tablecloths-conference-studio.jpg',
    '/home/tablecloths/tablecloths-box-fitted-studio.jpg',
    '/home/tablecloths/tablecloths-runner-studio.jpg',
    '/home/tablecloths/tablecloths-copper-beech.jpg',
    '/home/tablecloths/tablecloths-journey.jpg',
    '/home/tablecloths/tablecloths-reptile-shed.jpg',
    '/home/services/tablecloths.jpg',
    '/home/tablecloths/tablecloths-conference-studio.jpg',
    '/home/tablecloths/tablecloths-box-fitted-studio.jpg',
    '/home/tablecloths/tablecloths-runner-studio.jpg',
  ],

  useCases: [
    { title: 'Exhibitions and trade shows', text: 'Transform a trestle table into a branded stand that draws people in.' },
    { title: 'Corporate events and launches', text: 'Registration desks, product launches and conferences, done properly.' },
    { title: 'Markets and pop-ups', text: 'Stand out from neighbouring stalls with a branded cover.' },
    { title: 'Retail counters', text: 'A quick, low-cost way to smarten up a till point or reception desk.' },
    { title: 'Custom sizing', text: 'Reception desks, counters and non-standard tables measured to fit.' },
  ],

  optionsHeading: 'Choose your cover',
  optionsIntro: 'All three styles are available for 4ft, 6ft and 8ft trestle tables, with custom sizes made to order.',
  options: [
    {
      id: 'conference-cloth',
      title: 'Conference cloth (throw)',
      lead: 'A flat cloth that drapes over the table, our simplest option.',
      body: 'Offers less of a tailored look than a fitted cover but still gives your table a professional finish. Drapes over three or four sides depending on how much access you need underneath, with box pleats or rounded corners available.',
      image: '/home/tablecloths/tablecloths-conference-studio.jpg',
      specs: [
        { label: 'Size', value: '6ft: 228 x 335cm (90 x 132in). 8ft: 228 x 396cm (90 x 156in). Custom sizes available.' },
        { label: 'Colours', value: '19 stock colours including black, navy, royal blue, burgundy, apple green and lavender.' },
        { label: 'Price', value: 'From £17 + VAT.' },
      ],
    },
    {
      id: 'box-fitted',
      title: 'Box fitted table cover',
      lead: 'A fitted cover that sits snugly over the table for a tailored look.',
      body: 'Sewn corners give a crisp, tailored silhouette. The better choice when the table will be seen from multiple angles, such as a retail counter or an exhibition pod.',
      image: '/home/tablecloths/tablecloths-box-fitted-studio.jpg',
      specs: [
        { label: 'Size', value: '4ft: 76.2 x 121.9cm (30 x 48in). 6ft: 76.2 x 182.8cm (30 x 72in).' },
        { label: 'Colours', value: '22 stock colours including white, silver grey, pink, fuchsia and caribbean blue.' },
        { label: 'Price', value: 'From £29.50 + VAT.' },
      ],
    },
    {
      id: 'table-runner',
      title: 'Table runner',
      lead: 'Our most affordable way to add branding to a table you already have.',
      body: 'Lays over an existing cloth with a standard 30cm drop. A quick way to add impact without covering the whole table.',
      image: '/home/tablecloths/tablecloths-runner-studio.jpg',
      specs: [
        { label: 'Size', value: '100 x 228cm, 100 x 335cm or 100 x 396cm. Custom sizes available.' },
        { label: 'Colours', value: 'Same stock colour range as our tablecloths.' },
        { label: 'Price', value: 'From £27 + VAT.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Direct to Film (DTF)',
      text: 'Fabric ink is printed onto a film, then heat pressed onto the fabric. The best choice for multiple colours, small text, gradients, photos and neon, metallic, glitter or holographic finishes, with no set-up fee or minimum order.',
    },
    {
      title: 'Heat Transfer Vinyl (HTV)',
      text: 'Artwork is cut from a roll of coloured vinyl with heat-sensitive glue on the back, then heat pressed on. Sharp, crisp edges suit simple designs, logos and lettering, including neon, metallic, glitter and holographic colours.',
    },
  ],

  gallery: [
    '/home/tablecloths/tablecloths-copper-beech.jpg',
    '/home/tablecloths/tablecloths-journey.jpg',
    '/home/tablecloths/tablecloths-reptile-shed.jpg',
    '/home/services/tablecloths.jpg',
    '/home/tablecloths/tablecloths-conference-studio.jpg',
    '/home/tablecloths/tablecloths-box-fitted-studio.jpg',
    '/home/tablecloths/tablecloths-runner-studio.jpg',
  ],

  steps: [
    {
      title: 'Choose your canvas',
      text: 'Drop off a tablecloth you already own or pick one of our tried and tested covers.',
    },
    {
      title: 'Get a free quote and mock-up',
      text: 'Send over your logo and our design team will put together a visual of how it could look.',
    },
    {
      title: 'Approve your order',
      text: 'Once you sign off the visual, production usually takes 5 to 10 working days.',
    },
    {
      title: 'Order up',
      text: 'Collect from our Kidderminster shop or have it delivered from £6.',
    },
  ],

  info: [
    {
      title: 'Customisation pricing',
      text: 'Pricing is per customisation area, applied to as many sides of the cover as you need.',
      bullets: ['Up to 50 x 50cm: £35 + VAT', 'Up to 100 x 50cm: £60 + VAT', 'Up to 150 x 50cm: £80 + VAT', 'Discounts available on multiple covers'],
    },
    {
      title: 'Turnaround',
      text: 'Typically 5 to 10 working days from approval. Got an event coming up sooner? Talk to us and we will do our best to get you sorted in time.',
    },
    {
      title: 'Care and washing',
      text: 'Our covers are machine washable, so they are easy to keep looking sharp between events.',
      bullets: ['Machine wash on a cool cycle with a mild detergent', 'Avoid bleach and fabric softener', 'Air dry or tumble dry on low', 'Store folded and steam or lightly iron before your next event'],
    },
  ],

  cta: {
    title: 'Get your free tablecloth quote.',
    text: 'Tell us about your event and we will recommend the right cover, colour and customisation.',
    image: '/home/tablecloths/tablecloths-copper-beech.jpg',
  },
};

export default tablecloths;
